import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { MarketPanel } from '@/components/MarketPanel'
import { SymbolPicker } from '@/components/SymbolPicker'
import { useLocalState } from '@/hooks/useLocalState'
import type { HeldSymbol } from '@/lib/positions'
import { useViewport } from '@/hooks/useViewport'
import type {
  Instrument,
  LiveQuote,
  PlanRequest,
  PlanResponse,
  Side,
  SizingMode,
} from '@/lib/types'

/**
 * Grille de graphiques, avec plein écran.
 *
 * **Le plein écran est fait en CSS, pas avec l'API `requestFullscreen`.**
 * Safari sur iPhone ne l'implémente pas — l'appeler ne ferait rien du tout, et
 * le bouton semblerait cassé sans le moindre message. Un panneau en
 * `position: fixed` couvrant la fenêtre donne le même résultat partout,
 * navigateur de bureau et application installée sur l'écran d'accueil compris.
 *
 * Les niveaux déplaçables n'apparaissent que sur le graphique dont la paire
 * est celle du ticket : sur les autres, ils désigneraient des prix sans rapport
 * avec l'ordre en préparation.
 */

/** Un panneau de la grille : son identité et la paire qu'il affiche. */
interface Pane {
  id: string
  symbol: string
}

const LAYOUTS = [1, 2, 4] as const
type Layout = (typeof LAYOUTS)[number]

export function ChartGrid({
  instruments,
  favourites,
  held,
  recents,
  ticketSymbol,
  quotes,
  onBrowsingChange,
  quoteAge = null,
  brokerReady,
  side,
  stopLossPips,
  takeProfitPips,
  riskAmount,
  rewardAmount,
  currency,
  onChangeStop,
  onChangeTarget,
  onChangeSide,
  maxRewardRatio,
  orderRefusal,
  request,
  plan,
  onSelectTicketSymbol,
  onToggleFavourite,
  lots,
  onChangeLots,
  sizingMode,
  onChangeSizingMode,
  riskAmountInput,
  onChangeRiskAmount,
  riskPercentInput,
  onChangeRiskPercent,
  leading,
}: {
  instruments: Instrument[]
  favourites: string[]
  /** Positions ouvertes par paire, marquées dans le sélecteur. */
  held?: Record<string, HeldSymbol>
  /** Dernières paires choisies, passées au sélecteur. */
  recents?: string[]
  /** Prévient l'écran qu'un sélecteur est ouvert : il lui faut tous les prix. */
  onBrowsingChange?: (open: boolean) => void
  /** Paire du ticket d'ordre : c'est elle qui porte les niveaux. */
  ticketSymbol: string
  quotes: Record<string, LiveQuote>
  /** Âge de la dernière cotation reçue par le serveur, en secondes. */
  quoteAge?: number | null
  brokerReady: boolean
  side: Side
  stopLossPips: string
  takeProfitPips: string
  riskAmount: string | null
  rewardAmount: string | null
  currency: string
  onChangeStop: (pips: string) => void
  onChangeTarget: (pips: string) => void
  /** Bascule le sens quand un niveau change de côté sur le graphique. */
  onChangeSide: (side: Side) => void
  /** Ratio gain/risque maximum autorisé sur la saisie (garde-fou serveur). */
  maxRewardRatio: number
  /** Refus calculé par l'écran (plafond du total en jeu). */
  orderRefusal?: string | null
  /** Ordre en préparation : alimente le bouton d'achat/vente du graphique. */
  request: PlanRequest | null
  plan: PlanResponse | null
  onSelectTicketSymbol: (symbol: string) => void
  onToggleFavourite: (symbol: string) => void
  /** Volume du ticket, ajustable depuis le graphique. */
  lots: string
  onChangeLots: (lots: string) => void
  /** Dimensionnement du ticket, réglable depuis le graphique. */
  sizingMode: SizingMode
  onChangeSizingMode: (mode: SizingMode) => void
  riskAmountInput: string
  onChangeRiskAmount: (amount: string) => void
  riskPercentInput: string
  onChangeRiskPercent: (percent: string) => void
  /**
   * Contenu placé à gauche de la barre d'outils (la barre des paires).
   *
   * Les deux occupaient chacun une ligne pleine largeur, soit une centaine de
   * pixels pris au graphique en haut de l'écran. Ils tiennent sur la même.
   */
  leading?: ReactNode
}) {
  const [layout, setLayout] = useLocalState<Layout>('chartLayout', 1)
  const [panes, setPanes] = useLocalState<Pane[]>('chartPanes', [
    { id: 'main', symbol: 'EURUSD' },
  ])
  const [fullscreen, setFullscreen] = useState<string | null>(null)
  const [picking, setPicking] = useState<string | null>(null)

  // L'écran au-dessus décide des abonnements : il doit savoir quand une liste
  // de prix est à l'écran, sinon elle n'affiche que des tirets.
  useEffect(() => {
    onBrowsingChange?.(picking !== null)
  }, [picking, onBrowsingChange])
  const { width, height, isWide } = useViewport()
  /*
   * Un seul graphique sur téléphone.
   *
   * Deux ou quatre graphiques empilés sur 375 px donnent des vignettes où l'on
   * ne place plus rien : ni un stop à la bonne dizaine de pips, ni même une
   * lecture de la tendance. Le réglage reste mémorisé — revenir sur
   * l'ordinateur retrouve sa disposition — mais il n'est ni appliqué ni
   * proposé ici.
   */
  const narrow = width < 768
  const effectiveLayout: Layout = narrow ? 1 : layout

  /*
   * Hauteur des graphiques : mesurée, pas devinée.
   *
   * 300 px figés conviennent à un téléphone et gaspillent la moitié d'un écran
   * de bureau ; `lightweight-charts` veut de toute façon une hauteur en
   * pixels, pas une classe CSS. On mesure donc où commence la grille, et on
   * lui donne ce qui reste de la fenêtre.
   *
   * Mesurer plutôt que soustraire une constante, parce que ce qui précède
   * varie : bandeau du gardien, avertissement de démonstration, message
   * d'erreur, hauteur du bandeau selon la largeur… Une constante serait fausse
   * la moitié du temps.
   */
  const grid = useRef<HTMLDivElement>(null)
  const [available, setAvailable] = useState(0)
  /*
   * Ce que la carte ajoute autour du canevas : nom de la paire, sélecteur
   * d'intervalles, cotation, barre d'outils, et le bandeau de démonstration
   * quand il est là. Mesuré, pas estimé — une constante se trompait de 160 px
   * selon que la démonstration était active ou non, et le bouton d'achat
   * passait sous la ligne de flottaison.
   */
  const [chrome, setChrome] = useState(190)

  useLayoutEffect(() => {
    const measure = () => {
      const box = grid.current?.getBoundingClientRect()
      if (!box) return

      // La mesure se fait DANS le cadre défilant de l'application, pas dans la
      // fenêtre : sinon elle change à chaque défilement et la hauteur du
      // graphique se mettrait à sauter pendant qu'on fait défiler.
      const scroller = grid.current?.closest<HTMLElement>('[data-scroll]')
      if (scroller) {
        const top = box.top - scroller.getBoundingClientRect().top + scroller.scrollTop
        setAvailable(Math.max(scroller.clientHeight - top, 260))
        return
      }
      setAvailable(Math.max(window.innerHeight - (box.top + window.scrollY), 260))
    }
    measure()
    window.addEventListener('resize', measure)
    // Le contenu au-dessus peut apparaître ou disparaître (bandeau du gardien,
    // avertissement de démonstration) : on suit aussi la hauteur de la page.
    const observer = new ResizeObserver(measure)
    observer.observe(document.body)
    return () => {
      window.removeEventListener('resize', measure)
      observer.disconnect()
    }
  }, [layout])

  const rows = effectiveLayout === 1 ? 1 : Math.ceil(effectiveLayout / 2)
  const clamp = (value: number, low: number, high: number) =>
    Math.round(Math.min(Math.max(value, low), high))

  // La carte mesure `chrome + paneHeight` : on en déduit l'habillage réel.
  // Le seuil de 4 px évite la boucle sans fin des arrondis.
  useLayoutEffect(() => {
    const card = grid.current?.querySelector('section')
    if (!card) return
    const measured = card.getBoundingClientRect().height - paneHeight
    if (measured > 0 && Math.abs(measured - chrome) > 4) setChrome(measured)
  })

  /*
   * Deux règles, parce que les deux écrans ne se lisent pas pareil.
   *
   * Sur ORDINATEUR, tout doit tenir sans défilement : on prend la place
   * mesurée. Sur TÉLÉPHONE, vouloir tout faire entrer donnait un graphique de
   * 200 px — plus petit qu'avant — parce que les bandeaux du haut consomment
   * déjà les deux tiers de l'écran. Une page de téléphone est faite pour
   * défiler : on prend une fraction franche de la hauteur.
   */
  const paneHeight = isWide
    ? // Un plancher de 360 px : sur un portable de 900 px de haut, la place
      // réellement libre tomberait à 150 px. Mieux vaut faire défiler la page
      // de deux centimètres que travailler sur un graphique de timbre-poste.
      clamp((available - 16) / rows - chrome, 360, 780)
    : clamp((height * 0.44) / Math.min(rows, 2), 240, 420)

  // Le premier panneau suit toujours la paire du ticket : c'est le graphique
  // sur lequel on place ses niveaux.
  useEffect(() => {
    setPanes((current) => {
      if (current.length === 0) return [{ id: 'main', symbol: ticketSymbol }]
      if (current[0].symbol === ticketSymbol) return current
      return [{ ...current[0], symbol: ticketSymbol }, ...current.slice(1)]
    })
  }, [ticketSymbol, setPanes])

  // La disposition impose le nombre de panneaux : on complète ou on tronque.
  useEffect(() => {
    setPanes((current) => {
      if (current.length === layout) return current
      if (current.length > layout) return current.slice(0, layout)

      const used = new Set(current.map((pane) => pane.symbol))
      const extra: Pane[] = []
      for (const instrument of instruments) {
        if (current.length + extra.length >= layout) break
        if (used.has(instrument.name)) continue
        used.add(instrument.name)
        extra.push({ id: `pane-${Date.now()}-${extra.length}`, symbol: instrument.name })
      }
      return [...current, ...extra]
    })
  }, [layout, instruments, setPanes])

  // Le plein écran verrouille le défilement de la page : sans cela, la page
  // continue de bouger derrière le panneau pendant qu'on manipule les niveaux.
  // La touche Échap le referme, comme toute vue modale au clavier.
  useEffect(() => {
    if (!fullscreen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(null)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKey)
    }
  }, [fullscreen])

  function setPaneSymbol(id: string, symbol: string) {
    if (id === panes[0]?.id) {
      // Changer la paire du premier panneau, c'est changer celle du ticket.
      onSelectTicketSymbol(symbol)
      return
    }
    setPanes((current) =>
      current.map((pane) => (pane.id === id ? { ...pane, symbol } : pane)),
    )
  }

  function removePane(id: string) {
    setPanes((current) => current.filter((pane) => pane.id !== id))
    setLayout((current) => (LAYOUTS.find((value) => value < current) ?? 1) as Layout)
  }

  function renderPane(pane: Pane, height: number, isFullscreen: boolean) {
    return (
      <MarketPanel
        key={pane.id}
        paneId={pane.id}
        symbol={pane.symbol}
        instrument={instruments.find((item) => item.name === pane.symbol)}
        quote={quotes[pane.symbol] ?? null}
        held={held?.[pane.symbol]}
        quoteAge={quoteAge}
        brokerReady={brokerReady}
        side={side}
        stopLossPips={stopLossPips}
        takeProfitPips={takeProfitPips}
        riskAmount={riskAmount}
        rewardAmount={rewardAmount}
        currency={currency}
        onChangeStop={onChangeStop}
        onChangeTarget={onChangeTarget}
        onChangeSide={onChangeSide}
        maxRewardRatio={maxRewardRatio}
        orderRefusal={orderRefusal}
        // L'ordre en préparation ne vaut que pour la paire du ticket : sur un
        // autre panneau, le bouton enverrait un ordre sur la mauvaise paire.
        request={pane.symbol === ticketSymbol ? request : null}
        plan={pane.symbol === ticketSymbol ? plan : null}
        chartHeight={height}
        // Les niveaux ne concernent que la paire du ticket.
        showLevels={pane.symbol === ticketSymbol}
        fullscreen={isFullscreen}
        // En plein écran, la barre du haut porte déjà « Fermer » : une
        // seconde sortie, plus petite, ne ferait qu'ajouter du doute.
        onToggleFullscreen={isFullscreen ? undefined : () => setFullscreen(pane.id)}
        onClose={panes.length > 1 && !isFullscreen ? () => removePane(pane.id) : undefined}
        onPickSymbol={() => setPicking(pane.id)}
        lots={lots}
        onChangeLots={onChangeLots}
        sizingMode={sizingMode}
        onChangeSizingMode={onChangeSizingMode}
        riskAmountInput={riskAmountInput}
        onChangeRiskAmount={onChangeRiskAmount}
        riskPercentInput={riskPercentInput}
        onChangeRiskPercent={onChangeRiskPercent}
      />
    )
  }

  const expanded = panes.find((pane) => pane.id === fullscreen)

  return (
    <>
      {/* Barre d'outils : paire à gauche, nombre de graphiques à droite. */}
      <div className="flex items-stretch overflow-hidden rounded-lg border border-border bg-panel">
        {leading && <div className="min-w-0 flex-1">{leading}</div>}
        {/* Masqué sur téléphone : un seul graphique y est possible. */}
        <div
          className={`hidden shrink-0 items-center gap-0.5 px-1.5 py-1.5 md:flex sm:gap-1 sm:px-2 ${
            leading ? 'border-l border-border' : ''
          }`}
        >
          <span className="mr-1 hidden text-[11px] uppercase tracking-wide text-ink-muted sm:inline">
            Vues
          </span>
          {LAYOUTS.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setLayout(value)}
              aria-label={`${value} graphique${value > 1 ? 's' : ''}`}
              className={`min-h-touch w-8 rounded text-[12px] font-semibold transition-colors sm:w-10 ${
                layout === value
                  ? 'bg-accent text-white'
                  : 'bg-elevated text-ink-muted hover:bg-border/60'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      <div
        ref={grid}
        className={
          effectiveLayout === 1
            ? 'flex flex-col gap-3'
            : 'grid grid-cols-1 gap-3 sm:grid-cols-2'
        }
      >
        {panes.slice(0, effectiveLayout).map((pane) => renderPane(pane, paneHeight, false))}
      </div>

      {/* Plein écran : panneau fixe couvrant la fenêtre. */}
      {expanded && (
        <div className="safe-top safe-bottom fixed inset-0 z-40 flex flex-col bg-base">
          {/*
            Barre de sortie FIXE, en tête du plein écran.
            
            L'icône ⤡ du panneau ne suffisait pas : le plein écran recouvre la
            barre d'onglets, et cette icône pouvait défiler hors de vue. On se
            retrouvait enfermé dans le graphique, sans moyen de revenir aux
            positions ni aux statistiques. Un bouton pleine hauteur, toujours
            visible, ferme toute discussion.
          */}
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-panel px-2 py-1.5">
            <span className="num truncate text-[15px] font-semibold text-ink-strong">
              {expanded.symbol}
            </span>
            <button
              type="button"
              onClick={() => setFullscreen(null)}
              className="min-h-touch shrink-0 rounded-md border border-border bg-elevated px-4 text-[13px] font-semibold text-ink hover:bg-border/60 active:bg-border"
            >
              ✕ Fermer
            </button>
          </div>

          {/* Le contenu défile sous la barre, jamais par-dessus. */}
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {renderPane(expanded, Math.max(height - 290, 260), true)}
          </div>
        </div>
      )}

      {picking && (
        <SymbolPicker
          instruments={instruments}
          value={panes.find((pane) => pane.id === picking)?.symbol ?? ticketSymbol}
          favourites={favourites}
          quotes={quotes}
          held={held}
          recents={recents}
          onSelect={(symbol) => setPaneSymbol(picking, symbol)}
          onToggleFavourite={onToggleFavourite}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  )
}
